import React, { useEffect, useContext, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { TicketSchedulerForm } from '@nile/mangifera/src/Components/Forms';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/client';
import { CREATE_TICKET_SCHEDULE } from '@nile/graphql/src';
import {
  AppContext,
  ticketScheduleConstraints,
  toast,
  validateForm,
  useProduct,
  COMMON_CONSTANTS
  // withErrorBoundary
} from '@nile/components';
import { cloneDeep, isEmpty } from 'lodash';
import DOMPurify from 'dompurify';
import { Button, ButtonGroup, Page, Modal } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import withErrorBoundary from '../../../../components/src/HOC/withErrorBoundary';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import { removePropertiesForItProduct } from '@nile/itsm';
import { useGetDefaultTicketType } from '@nile/mangifera/src/Utils/Hooks';

const SchedulerContainer = styled.div`
  margin-top: -20px;
  padding-bottom: 12px;
`;

const formInputKeys = [
  'status',
  'priority',
  'impact',
  'urgency',
  'category',
  'subcategory',
  'cause',
  'subcause',
  'resolutionCode',
  'customFields',
  'ticketType'
];

const ticketDataObjectKeys = [
  'subject',
  'description',
  'ticketType',
  'client',
  'requester',
  'technician',
  'techGroup'
];

const TicketScheduleModal = (props) => {
  const appContext = useContext(AppContext);
  const [schedulerdata, setSchedulerdata] = useState({
    name: '',
    description: '',
    isRecursive: false,
    errors: {},
    schedule: {
      frequency: 'ONCE',
      data: {
        startDateAndTime: dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss')
      }
    }
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const currentProduct = useProduct();
  const { defaultTicketType } = useGetDefaultTicketType({});
  const [saveTicketScheduleWithInterceptor] = useMutation(
    CREATE_TICKET_SCHEDULE,
    {
      onCompleted({ createTicketSchedule }) {
        /* Close Modal once a ticket has been scheduled */
        if (createTicketSchedule) {
          setIsLoading(false);
          toast('success', 'app.toast.newScheduledTicket.creation.success');
          props.toggleScheduleModal();
        }
      },
      onError() {
        setIsLoading(false);
      }
    }
  );
  async function saveTicketSchedule(variables) {
    try {
      const updatedPayload = removePropertiesForItProduct(
        variables,
        'saveTicketSchedule',
        currentProduct
      );
      return await saveTicketScheduleWithInterceptor(updatedPayload);
    } catch (ex) {
      return ex;
    }
  }

  const saveScheduledTicket = () => {
    const { ticket, template, selectedFormTemplate } = props?.ticketData;
    setIsLoading(true);
    /* ask backend to change id to ticketId */
    if (ticket.ticketId) {
      ticket.id = ticket.ticketId;
    }
    ticket.description = dompurifier(template.content);
    // if (
    //   selectedFormTemplate &&
    //   selectedFormTemplate?.standardOperatingProcedure?.sopId
    // ) {
    //   ticket.sop = {
    //     sopId: selectedFormTemplate.standardOperatingProcedure.sopId
    //   };
    // }
    const formToValidate = cloneDeep({
      ...ticket,
      name: schedulerdata?.name,
      frequency: schedulerdata.schedule.frequency,
      ...schedulerdata.schedule.data
    });
    const validationResult = validateForm(
      formToValidate,
      ticketScheduleConstraints(formToValidate, isMSPProduct)
    );
    if (validationResult) {
      setErrors(validationResult);
      setIsLoading(false);
      return;
    }
    setErrors({});

    if (saveTicketSchedule) {
      const { name, description, schedule } = schedulerdata;
      const ticketDetails = {
        subject: props?.ticketData?.ticket?.subject,
        description: props?.ticketData?.ticket?.description,
        requester: !isEmpty(props?.ticketData?.ticket?.requester)
          ? props?.ticketData?.ticket?.requester
          : null,
        client: { accountId: props?.ticketData?.ticket?.client?.accountId },
        site: props?.ticketData?.ticket?.site?.id
          ? { id: props?.ticketData?.ticket?.site?.id }
          : null
      };
      if (!isMSPProduct) {
        ticketDetails.department = props?.ticketData?.ticket?.department
          ?.businessFunctionId
          ? {
              businessFunctionId:
                props?.ticketData?.ticket?.department?.businessFunctionId
            }
          : null;
      }
      formInputKeys.forEach((key) => {
        ticketDetails[key] = props.ticketData?.ticket?.[key] || null;
      });
      if (!ticketDetails.ticketType) {
        ticketDetails.ticketType = defaultTicketType || 'Incident';
      }
      if (props?.ticketData?.ticket?.techGroup) {
        ticketDetails.techGroup = {
          groupId: props?.ticketData?.ticket?.techGroup?.groupId
        };
      }
      if (props?.ticketData?.ticket?.technician) {
        ticketDetails.technician = {
          userId: props?.ticketData?.ticket?.technician?.userId
        };
      }
      if (props?.ticketData?.template?.attachments.length) {
        const attachmentsInput = props?.ticketData?.template?.attachments.map(
          (att) => {
            const { fileName, fileSize, originalFileName } = att;
            return {
              fileName,
              fileSize: fileSize.toString(),
              originalFileName
            };
          }
        );
        ticketDetails.addAttachments = attachmentsInput;
      }
      const inputParams = {
        name: name.trim(),
        description,
        data: ticketDetails,
        fromTicket: true,
        schedule
      };
      if (props?.ticketData?.selectedFormTemplate?.formId) {
        inputParams.form = {
          formId: props?.ticketData?.selectedFormTemplate?.formId
        };
      }

      // if(props?.ticketData?.ticket?)

      saveTicketSchedule({
        variables: { input: inputParams },
        input: { input: inputParams }
      });
    }
  };

  useEffect(() => {
    if (schedulerdata.isRecursive) {
      if (schedulerdata?.schedule?.frequency === 'ONCE') {
        setSchedulerdata({
          ...schedulerdata,
          schedule: {
            ...schedulerdata.schedule,
            frequency: 'DAILY',
            data: {
              ...schedulerdata.schedule.data,
              repeatInterval: '1'
            }
          }
        });
      }
    } else {
      setSchedulerdata({
        ...schedulerdata,
        schedule: { ...schedulerdata.schedule, frequency: 'ONCE' }
      });
    }
  }, [schedulerdata.isRecursive]);

  return (
    <Modal
      showCloseButton={false}
      large
      open={props.scheduleModalOpen}
      noScroll
    >
      <Page
        title={<FormattedMessage id="admin.stepnavi.ticketScheduler" />}
        variant="primary"
        contentHeight="120px"
        primaryAction={
          <ButtonGroup>
            <Button plainSecondary onClick={props.toggleScheduleModal}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              onClick={saveScheduledTicket}
              icon={<SVGIcon name="Save" />}
              loading={isLoading}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        }
      >
        <SchedulerContainer>
          <TicketSchedulerForm
            errors={errors}
            updateScheduleData={setSchedulerdata}
            scheduleData={schedulerdata}
          />
        </SchedulerContainer>
      </Page>
    </Modal>
  );
};

export default withErrorBoundary(injectIntl(TicketScheduleModal));
